import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AccessManagementRole,
  AccessManagementRolePermissionRecord,
  AccessManagementRoleRecord,
} from '@cdw-ae/common-data';
import {
  ApiResponse,
  ICdwSearchGridDataService,
} from '@cdw-ae/common-ui-components-search-grid';
import {
  IGetSqlDataParams,
  SelectedFilterField,
  SqlResponse,
} from '@cdw-ae/filter-bar-interfaces';
import { ROLES_ROUTES } from '@cdw-ae/routes-access-api';
import { getQueryStringFromParams } from '@cdw-ae/routes-shared-api';
import { catchError, map, Observable, of } from 'rxjs';
import { ApiUrlBuilderService } from '../../../../../../common/services/api-url-builder.service';

export const httpOptions: { headers: HttpHeaders } = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'x-cdw-app-name': 'Gopher for Chrome WebApp',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class RolesDataService
  implements ICdwSearchGridDataService<AccessManagementRoleRecord>
{
  constructor(
    private httpClient: HttpClient,
    private readonly urlBuilder: ApiUrlBuilderService
  ) {}

  public fetchSqlData(
    params: IGetSqlDataParams
  ): Observable<SqlResponse<AccessManagementRoleRecord>> {
    const { activeFilters, startRow, endRow }: IGetSqlDataParams = params;
    return this.getRoles(activeFilters, startRow, endRow);
  }

  public getRoles(
    activeFilters?: SelectedFilterField[],
    startRow?: number,
    endRow?: number
  ): Observable<SqlResponse<AccessManagementRoleRecord>> {
    const url = this.urlBuilder.backendApi('roles', ROLES_ROUTES.ROOT);
    const queryString = getQueryStringFromParams({
      activeFilters,
      startRow,
      endRow,
    });
    const fullUrl = queryString ? `${url}?${queryString}` : url;
    return this.httpClient.get<SqlResponse<AccessManagementRoleRecord>>(
      fullUrl
    ).pipe(
      catchError(() => of({ data: [], itemTotal: 0 } as SqlResponse<AccessManagementRoleRecord>))
    );
  }

  public getRoleById(
    roleId: number
  ): Observable<{
    success: boolean;
    data: {
      role: AccessManagementRoleRecord;
      rolePermissions: AccessManagementRolePermissionRecord[];
    };
  }> {
    const url = this.urlBuilder.backendApi('roles', ROLES_ROUTES.GET_BY_ID, { id: roleId });
    return this.httpClient
      .get<{
        role: AccessManagementRoleRecord;
        rolePermissions: AccessManagementRolePermissionRecord[];
      }>(url, httpOptions)
      .pipe(
        map((res) => ({
          success: true,
          data: res,
        })),
        catchError(() => of({ success: false, data: null as never }))
      );
  }

  public updateRole(
    role: Partial<AccessManagementRole>,
    permissionIds: number[],
    roleId: number,
    customerId: number
  ): Promise<Observable<ApiResponse<AccessManagementRoleRecord>>> {
    const url = this.urlBuilder.backendApi('roles', ROLES_ROUTES.GET_BY_ID, { id: roleId });
    return Promise.resolve(
      this.httpClient
        .put<AccessManagementRoleRecord>(
          url,
          { role, permissionIds, roleId, customerId },
          httpOptions
        )
        .pipe(
          map(
            (res): ApiResponse<AccessManagementRoleRecord> => ({
              success: true,
              data: [res],
            })
          ),
          catchError(() => of({ success: false, data: [] } as ApiResponse<AccessManagementRoleRecord>))
        )
    );
  }

  public canDeleteRole(roleId: number): Observable<{ canDelete: boolean; message?: string }> {
    const url = this.urlBuilder.backendApi('roles', ROLES_ROUTES.CAN_DELETE, { id: roleId });
    return this.httpClient.get<{ canDelete: boolean; message?: string }>(
      url,
      httpOptions
    ).pipe(
      catchError(() => of({ canDelete: false }))
    );
  }

  public deleteRole(roleId: number): Promise<Observable<{ success: boolean }>> {
    const url = this.urlBuilder.backendApi('roles', ROLES_ROUTES.GET_BY_ID, { id: roleId });
    return Promise.resolve(
      this.httpClient.delete<{ success: boolean }>(
        url,
        {
          ...httpOptions,
          body: { roleId },
        }
      ).pipe(
        catchError(() => of({ success: false }))
      )
    );
  }

  public addRole(
    role: Partial<AccessManagementRole>,
    permissionIds: number[]
  ): Observable<ApiResponse<AccessManagementRoleRecord>> {
    const url = this.urlBuilder.backendApi('roles', ROLES_ROUTES.ROOT);
    return this.httpClient
      .post(
        url,
        { role, permissionIds },
        httpOptions
      )
      .pipe(
        map((res) => ({
          success: true,
          data: [res as AccessManagementRoleRecord],
        })),
        catchError(() => of({ success: false, data: [] as AccessManagementRoleRecord[] }))
      );
  }
}
